import { useEffect, useRef, useState } from 'react'
import { aOrganization, useAtomValue } from '~/atoms'
import {
  buildGooglePlacePredictionRequestKey,
  createGoogleLocationRestriction,
  getGooglePlaceData,
  type GooglePlacePredictionOptions,
  getGooglePlacePredictions,
  getGooglePlaceSearchBoundsSignature,
  isGooglePlacePredictionRequestPending,
  rankGooglePredictions,
} from '~/utils/googleMapsPlaces'

export default function useGeocodeSearch({
  enabled = true,
  locationBias,
  preferredStateAbbrs,
}: {
  enabled?: boolean
  locationBias?: GooglePlacePredictionOptions['locationBias']
  preferredStateAbbrs?: readonly string[]
} = {}) {
  const organization = useAtomValue(aOrganization)
  const placeSearchBounds = organization?.options.placeSearchBounds
  const placeSearchBoundsSignature = getGooglePlaceSearchBoundsSignature(
    placeSearchBounds
  )
  const locationBiasSignature = JSON.stringify(locationBias ?? null)
  const preferredStateAbbrsSignature = JSON.stringify(preferredStateAbbrs ?? [])
  const locationSearchSignature = locationBias
    ? `bias:${locationBiasSignature}:states:${preferredStateAbbrsSignature}`
    : `bounds:${placeSearchBoundsSignature}:states:${preferredStateAbbrsSignature}`
  const [search, setSearch] = useState<string>('')
  const [isError, setIsError] = useState(false)
  const [settledRequestKey, setSettledRequestKey] = useState<string | null>(null)
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestGenerationRef = useRef(0)
  const requestKey =
    enabled && search.length > 0
      ? buildGooglePlacePredictionRequestKey(
          search,
          locationSearchSignature
        )
      : null
  const isLoading = isGooglePlacePredictionRequestPending(
    requestKey,
    settledRequestKey
  )

  useEffect(() => {
    const requestGeneration = ++requestGenerationRef.current

    if (debounceTimeout.current !== null) {
      clearTimeout(debounceTimeout.current)
      debounceTimeout.current = null
    }

    const cleanup = () => {
      ++requestGenerationRef.current
      if (debounceTimeout.current !== null) {
        clearTimeout(debounceTimeout.current)
        debounceTimeout.current = null
      }
    }

    if (!requestKey) {
      setPredictions([])
      setIsError(false)
      setSettledRequestKey(null)
      return cleanup
    }

    setSettledRequestKey(null)
    setIsError(false)
    debounceTimeout.current = setTimeout(async () => {
      try {
        const requestBounds = locationBias
          ? undefined
          : (JSON.parse(placeSearchBoundsSignature) as number[][] | null)
        const locationRestriction = createGoogleLocationRestriction(requestBounds)
        const nextPredictions = await getGooglePlacePredictions(search, {
          locationBias,
          locationRestriction,
        })
        if (requestGenerationRef.current !== requestGeneration) return

        setPredictions(
          locationBias || preferredStateAbbrs?.length
            ? rankGooglePredictions(nextPredictions, search, 'unknown', {
                preferredStateAbbrs,
              })
            : nextPredictions
        )
        setIsError(false)
      } catch {
        if (requestGenerationRef.current !== requestGeneration) return

        setPredictions([])
        setIsError(true)
      } finally {
        if (requestGenerationRef.current === requestGeneration) {
          setSettledRequestKey(requestKey)
        }
      }
    }, 200)

    return cleanup
  }, [
    locationBias,
    locationSearchSignature,
    placeSearchBoundsSignature,
    preferredStateAbbrs,
    preferredStateAbbrsSignature,
    requestKey,
    search,
  ])

  const getPlaceData = async (
    prediction: google.maps.places.AutocompletePrediction
  ) => {
    if (!enabled) return null

    return getGooglePlaceData(prediction.place_id)
  }

  return {
    search,
    setSearch,
    isLoading,
    isError,
    predictions,
    setPredictions,
    getPlaceData,
  }
}
